import { AccountInfoType } from './account.type';
import { FeeFrequencyTypeEnum, FeeUnitChargeTypeEnum } from './common.enum';
import { OrgAttributes } from './common.type';
import { Constants } from './default-constants/workflow-constants.type';

export type AddFeeRequest = {
  name: string;
  currency: string;
  clientId?: string;
  accountId?: string;
  feeGroupId?: string;
  unitChargeType: FeeUnitChargeTypeEnum;
  unitChargeInfo: {
    feeCharge: number;
    minimumFeeAmount?: number;
    maximumFeeAmount?: number;
  };
  frequencyType: FeeFrequencyTypeEnum;
  frequencyInfo: {
    day?: string;
    weekDay?: string;
    month?: string;
    quarter?: string;
    startDate?: Date;
    endDate?: Date;
  };
};

export type FeeInfoType = {
  id: string;
  name: string;
  currency: string;
  feeGroupId?: string;
  unitChargeType: FeeUnitChargeTypeEnum;
  unitChargeInfo: {
    feeCharge: number;
    minimumFeeAmount?: number;
    maximumFeeAmount?: number;
  };
  frequencyType: FeeFrequencyTypeEnum;
  frequencyInfo: {
    day?: string;
    weekDay?: string;
    month?: string;
    quarter?: string;
    startDate?: Date;
    endDate?: Date;
  };
  status: string;
};

export type FeeGroup = {
  id: string;
  name: string;
  description: string;
  createdAt: string;
};

export type AddEditFeeRequestPayload = {
  id?: string;
  name: string;
  currency: string;
  feeGroupId: string;
  unitChargeType: string;
  unitChargeInfo: {
    feeCharge: string;
    minFeeAmount?: string;
    maxFeeAmount?: string;
  };
  frequencyType: string;
  frequencyInfo: {
    startDate: string;
    endDate?: string;
  };
  status?: string;
};

export type AddEditFeeGroupRequestPayload = {
  name: string;
  description: string;
};

export type FeeItem = {
  showMenuPopup?: boolean;
  id: string;
  feeName: string;
  currency: string;
  unitChargeType: string;
  feeCharge: string;
  minimumFeeAmount: string;
  maximumFeeAmount: string;
  status: string;
  createdAt: string;
  adHocFee?: {
    accountId: string;
    organisationId: string;
  };
  feeGroupId: {
    id: string;
    name: string;
    description: string;
    createdAt: string;
  };
  feeFrequencySettings: {
    id: string;
    frequencyType: string;
    startDate: string;
    endDate?: string;
    createdAt: string;
  };
};

export type FeeListingResponse<T> = {
  result: T[];
  limit: number;
  page: number;
  totalPages: number;
  totalItems: number;
};
export type FeeGroupItem = {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  createdAt: string;
  clientLinked: number;
};
export type FeeGroupsListingResponse = {
  result: FeeGroupItem[];
  limit: number;
  page: number;
  totalPages: number;
  totalItems: number;
};

export type ClientListingResponse = {
  result: ClientDetails[];
  limit: number;
  page: number;
  totalPages: number;
  totalItems: number;
};

export type ClientDetails = {
  id: string;
  name: string;
  attributes: {
    address: {
      address1: string;
      address2: string;
      address3: string;
      address4: string;
      postalCode: string;
      country: string;
      city: string;
      state: string;
    };
    identificationNumber: string;
  };
  clientType: string;
  displayName: string;
  feeGroupName?: string;
  feeGroupId: string;
  accountList?: AccountInfoType[];
};

export type ChargedFeeItem = {
  id: string;
  feeChargedAmount: string;
  status: string;
  createdAt: string;
  feeDetailId: string;
  feeName: string;
  feeCurrency: string;
  unitChargeType: string;
  minimumFeeAmount: string;
  maximumFeeAmount: string;
  feeCharge: string;
  feeGroupId: string;
  feeGroupName: string;
  feeGroupIsDefault: boolean;
  feeFrequencySettingsId: string;
  frequencyType: string;
  organisationId: string;
  clientName: string;
  clientType: string;
  actionEnabled?: boolean;
};

export enum ListingTypeEnum {
  FEE_LISTING = 'fee-listing',
  CLIENT_LISTING = 'client-listing',
  GROUP_FEE_LISTING = 'group_fee-listing',
  GROUP_CLIENT_LISTING = 'group_client-listing',
  FEE_CHARGE_HISTORY_LISTING = 'fee-charge-history-listing',
  CLIENT_FEE_LISTING = 'client-fee-listing',
  CLIENT_FEE_CHARGED_HISTORY = 'client-fee-charged-history',
}

export const unitChargeOptions = [
  {
    value: FeeUnitChargeTypeEnum.PERCENTAGE_WITH_NO_MIN_MAX,
    title: 'Percentage with no Minimum/Maximum Fee',
  },
  {
    value: FeeUnitChargeTypeEnum.PERCENTAGE_WITH_MIN,
    title: 'Percentage with Minimum Fee',
  },
  {
    value: FeeUnitChargeTypeEnum.PERCENTAGE_WITH_MAX,
    title: 'Percentage with Maximum Fee',
  },
  {
    value: FeeUnitChargeTypeEnum.PERCENTAGE_WITH_MIN_MAX,
    title: 'Percentage with Minimum and Maximum Fee',
  },
  { value: FeeUnitChargeTypeEnum.FIXED_FEE, title: 'Fixed Fee' },
];
export const frequencyOptions = [
  { id: 'default', label: 'Select frequency' },
  { id: FeeFrequencyTypeEnum.ONE_OFF, label: 'One-off' },
  { id: FeeFrequencyTypeEnum.DAILY, label: 'Daily' },
  { id: FeeFrequencyTypeEnum.WEEKLY, label: 'Weekly' },
  { id: FeeFrequencyTypeEnum.MONTHLY, label: 'Monthly' },
  { id: FeeFrequencyTypeEnum.QUARTERLY, label: 'Quarterly' },
  { id: FeeFrequencyTypeEnum.ANNUALLY, label: 'Annual' },
];

const defaultTableHeaderSettingCssConfig = {
  tableHeaderBackgroundColor: 'var(--color-surface)',
  tableHeaderTextColor: 'var(--color-on-background)',
  tableHeaderFontSize: 16,
  tableHeaderFontUnit: 'px',
  tableHeaderFontFamily: 'Mulish',
};

const baseWidgetConfig = {
  css_config: {
    ...Constants.defaultBaseCssConfig,
    textAlignment: 'center',
    verticalAlignment: 'center',
    shadowBlur: 0,
    shadowSpread: 0,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    iconSize: 24,
  },
  widgetProperties: {
    icon: 'search',
    showEntity: false,
    textContent: '',
  },
  pageIndex: 0,
  zone: '',
  iconConfig: {
    leftIcon: '',
    rightIcon: '',
  },
  tableHeaderSetting: {
    css_config: defaultTableHeaderSettingCssConfig,
  },
  rowSettings: {
    rowBgColor: 'var(--color-background)',
    showErrorDuplicationRowBgColor: false,
  },
  tableGridSetting: {
    showHorizontalGridLines: true,
    showVerticalGridLines: false,
    gridLinesWidth: 1,
  },
  viewPaginationOption: ['05', '10', '20'],
  viewTableSize: 'p-datatable-lg',
  columns: [],
};

export type UpdateOrganisationAttributes = {
  name: string;
  attributes: OrgAttributes;
  displayName: string;
};

export type ClientDetailsType = {
  id: string;
  name: string;
  attributes: {
    address: {
      address1: string;
      address2: string;
      address3: string;
      address4: string;
      postalCode: string;
      country: string;
      city: string;
      state: string;
    };
    identificationNumber: string;
    phoneNumber: string;
  };
  clientType: string;
  displayName: string;
  feeGroupId: string;
  feeGroupName: string;
  accountList: AccountInfoType[];
};

export type AdHocFeeRequest = {
  name: string;
  currency: string;
  clientId: string;
  accountId: string;
  unitChargeType: string;
  unitChargeInfo: {
    feeCharge: number;
    minFeeAmount: number;
    maxFeeAmount: number;
  };
  frequencyType: string;
  frequencyInfo: {
    startDate: string;
    endDate?: string;
  };
  status?: string;
};
